import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Watch,
  Method,
  Event,
  EventEmitter,
} from '@stencil/core';
import { AlertLevel, AlertSize } from './types';
import { hideIfSlotEmpty } from '../../utils/slotHelpers';

export interface AhaAlert extends HTMLAhaAlertElement {}

/**
 * @slot heading - Replace the heading/title of the alert with custom markup.
 */
@Component({
  tag: 'aha-alert',
  styleUrl: 'aha-alert.css',
  shadow: true,
})
export class AhaAlert {
  @Element() host!: HTMLAhaAlertElement;

  /**
   * Deprecated use the heading slot instead
   */
  @Prop() heading?: string;

  @Watch('heading')
  watchHeading() {
    this.hideHeadingIfSlotEmpty(this.headingSlot);
  }

  /**
   * Controls the look and feel of the alert.
   *
   * Select the appropriate type for your use case:
   *
   *   - `info`: When providing neutral or supplementary information.
   *   - `ai`: When providing information about AI.
   *   - `success`: When indicating error-free completion of an action.
   *   - `warning`: When caution is required from the user.
   *   - `danger`: When indicating action failure or indicating caution relating to an unrecoverable or dangerous action.
   */
  @Prop({ reflect: true }) type: AlertLevel = 'info';

  /**
   * Controls the size of the alert.
   *
   * Select the appropriate size for your use case:
   *
   *   - `default`: Standard alert size with normal padding and font sizes.
   *   - `mini`: Compact alert size with reduced padding and smaller font sizes.
   */
  @Prop({ reflect: true }) size: AlertSize = 'default';

  /**
   * Controls the display of a dismissal icon on the alert that will destroy the
   * alert on click.
   *
   * TODO this is spelt wrong, deprecate and add `dismissible` instead?
   */
  @Prop({ reflect: true }) dismissable: boolean = false;

  @Prop({ reflect: true, attribute: 'hide-icon' }) hideIcon: boolean = false;

  /**
   * Triggers when the alert is dismissed.
   */
  @Event({
    eventName: 'aha-alert:dismissed',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  dismissedEvent!: EventEmitter<HTMLAhaAlertElement>;

  handleDismiss() {
    if (this.dismissable) {
      const event = this.dismissedEvent.emit(this.host);
      if (!event.defaultPrevented) {
        // @ts-ignore
        this.host.remove();
      }
    }
  }

  @Method()
  async hide() {
    this.handleDismiss();
  }

  private headingSlot?: any;
  private iconSlot?: any;
  private closeSlot?: any;

  hideHeadingIfSlotEmpty(slot: HTMLSlotElement) {
    if (!slot) return;
    const headingElem = slot.parentElement as HTMLElement;
    hideIfSlotEmpty(slot, headingElem);
  }

  handleHeadingSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this.hideHeadingIfSlotEmpty(slot);
  }

  connectedCallback() {
    this.handleDismiss = this.handleDismiss.bind(this);
    this.handleHeadingSlotChange = this.handleHeadingSlotChange.bind(this);
  }

  componentDidLoad() {
    this.hideHeadingIfSlotEmpty(this.headingSlot);
  }

  render() {
    let defaultIcon;
    switch (this.type) {
      case 'danger':
        defaultIcon = 'fa-regular fa-triangle-exclamation';
        break;
      case 'info':
        defaultIcon = 'fa-regular fa-info-circle';
        break;
      case 'success':
        defaultIcon = 'fa-solid fa-check';
        break;
      case 'warning':
        defaultIcon = 'fa-regular fa-circle-exclamation';
        break;
      case 'info-settings':
        defaultIcon = 'fa-regular fa-info-circle';
        break;
    }

    return (
      <Host role="alert">
        <div id="layout" part="layout">
          <div id="close" hidden={!this.dismissable} onClick={this.handleDismiss}>
            <slot ref={el => (this.closeSlot = el)} name="close">
              <button type="button" aria-label="Close" disabled={!this.dismissable}>
                <fa-icon class="fa-regular fa-times"></fa-icon>
              </button>
            </slot>
          </div>
          <div id="icon" hidden={this.hideIcon}>
            <slot ref={el => (this.iconSlot = el)} name="icon">
              {this.type === 'ai' ? (
                <aha-ai-icon />
              ) : (
                <fa-icon class={`aha-alert__default-icon ${defaultIcon} fa-fw`}></fa-icon>
              )}
            </slot>
          </div>
          <div id="content">
            <div id="heading">
              <slot
                ref={el => (this.headingSlot = el)}
                name="heading"
                onSlotchange={this.handleHeadingSlotChange}
              >
                {this.heading}
              </slot>
            </div>

            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}
